import { useCallback, useState } from 'react';
import { Alert, Group, Radio, Stack, Text, Textarea } from '@mantine/core';
import { CheckCircle2, AlertTriangle, Lightbulb, Send } from 'lucide-react';
import { Badge, Button, Card, Pill } from '../ui';
import { useSubmitTask } from '../../hooks/useLabs';
import type { GradingPolicy, LabTask, LabTaskProgress } from '../../api/types';
import { colors, neutral } from '../../theme/tokens';

/**
 * LabRunner — single-task submission form. Tracks per-task state in a small
 * local Map; submissions hit the server-side grader. Hints expand on demand.
 *
 * Performance: each LabRunner is mounted per task and only re-renders when its
 * own answer state changes. We avoid lifting state up to keep the lab page
 * stable across submissions.
 */

interface LabRunnerProps {
  labId: string;
  task: LabTask;
  /** 1-based index in the lab list. */
  index: number;
  /** Active enrollment ID for the current trainee. */
  enrollmentId?: number | undefined;
  /** Attempts state from the polled progress (attempts policy, 2026-09-18). */
  progress?: LabTaskProgress | undefined;
  policy?: GradingPolicy | null | undefined;
}

export function LabRunner({ labId, task, index, enrollmentId, progress, policy }: LabRunnerProps) {
  const [answer, setAnswer] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState<
    | null
    | { status: 'correct' | 'incorrect' | 'pending'; message: string; points: number }
  >(null);
  const [attempts, setAttempts] = useState<{ used: number; left: number | null; locked: boolean; next: number | null } | null>(null);
  const submit = useSubmitTask();

  // Progress from the server is the truth on load / refresh; a fresh submit
  // response overrides it until the next poll catches up.
  const used = attempts?.used ?? progress?.attempts ?? 0;
  const left = attempts ? attempts.left : progress?.attemptsLeft ?? (policy ? (policy.max_attempts > 0 ? Math.max(0, policy.max_attempts - used) : null) : null);
  const solved = feedback?.status === 'correct' || progress?.status === 'completed';
  const locked = solved || Boolean(attempts?.locked ?? progress?.locked);
  const maxAttempts = policy?.max_attempts ?? 0;

  const handleSubmit = useCallback(async () => {
    if (!answer.trim() || !enrollmentId) return;
    const result = await submit.mutateAsync({
      labId,
      taskId: task.id,
      enrollmentId,
      taskIndex: index - 1,
      answer,
    });
    setFeedback({
      status: result.status,
      message: result.feedback ?? (result.status === 'correct' ? 'Correct.' : 'Try again.'),
      points: result.pointsAwarded,
    });
    setAttempts({ used: result.attemptsUsed, left: result.attemptsLeft, locked: result.locked, next: result.nextPoints });
  }, [answer, submit, labId, task.id, enrollmentId, index]);

  return (
    <Card variant={feedback?.status === 'correct' ? 'elevated' : 'default'}>
      <Stack gap="sm">
        <Group justify="space-between" align="start" wrap="nowrap">
          <Group gap="md" align="start">
            <Text
              ff="JetBrains Mono"
              fw={700}
              size="sm"
              c={feedback?.status === 'correct' ? colors.neon : 'dimmed'}
              style={{ minWidth: 30 }}
            >
              {String(index).padStart(2, '0')}
            </Text>
            <Stack gap={4}>
              <Group gap="xs" align="center">
                <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                  {task.title}
                </Text>
                {feedback?.status === 'correct' ? (
                  <CheckCircle2 size={14} color={colors.neon} />
                ) : null}
              </Group>
              <Text size="xs" c="dimmed" maw={680}>
                {task.description}
              </Text>
            </Stack>
          </Group>
          <Pill variant="neutral" size="xs">
            {task.type}
          </Pill>
        </Group>

        {task.type === 'mcq' && task.options ? (
          <Radio.Group value={answer} onChange={setAnswer} aria-label={task.title}>
            <Stack gap="xs" mt="xs">
              {task.options.map((opt) => (
                <Radio
                  key={opt}
                  value={opt}
                  label={opt}
                  styles={{ label: { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 13 } }}
                />
              ))}
            </Stack>
          </Radio.Group>
        ) : (
          <Textarea
            value={answer}
            onChange={(e) => setAnswer(e.currentTarget.value)}
            placeholder="Type the exact value you recovered"
            minRows={2}
            autosize
            maxRows={5}
            aria-label={task.title}
            styles={{
              input: {
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 13,
                backgroundColor: colors.deep,
                borderColor: neutral.border,
              },
            }}
          />
        )}

        {task.hint ? (
          <Stack gap={4}>
            <Group gap={6}>
              <Button
                variant="ghost"
                size="xs"
                leftIcon={<Lightbulb size={12} />}
                onClick={() => setShowHint((v) => !v)}
              >
                {showHint ? 'Hide hint' : 'Show hint'}
              </Button>
            </Group>
            {showHint ? (
              <Alert color="amber" variant="light" icon={<Lightbulb size={14} />}>
                {task.hint}
              </Alert>
            ) : null}
          </Stack>
        ) : null}

        <Group justify="space-between">
          {feedback ? (
            <Group gap="xs">
              <Badge variant={feedback.status === 'correct' ? 'success' : 'danger'} size="xs">
                {feedback.status}
              </Badge>
              <Text size="xs" c="dimmed">
                {feedback.message}
              </Text>
              {feedback.points > 0 ? (
                <Text size="xs" c={colors.hudCyan} ff="JetBrains Mono">
                  +{feedback.points} pts
                </Text>
              ) : null}
            </Group>
          ) : (
            <span />
          )}
          <Group gap="sm" wrap="nowrap">
            <AttemptsBadge used={used} left={left} max={maxAttempts} locked={locked} solved={solved} policy={policy ?? null} next={attempts?.next ?? null} />
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Send size={12} />}
              onClick={handleSubmit}
              loading={submit.isPending}
              disabled={!answer.trim() || !enrollmentId || locked}
              title={!enrollmentId ? 'Start the lab first to submit answers' : locked && !solved ? 'No attempts left' : ''}
            >
              {solved ? 'Submitted' : locked ? 'Locked' : 'Submit'}
            </Button>
          </Group>
        </Group>

        {submit.isError ? (
          <Alert color="red" icon={<AlertTriangle size={14} />} variant="light">
            {(submit.error as Error).message}
          </Alert>
        ) : null}
      </Stack>
    </Card>
  );
}


/**
 * "attempt 2 of 3 · −25% per miss" — the attempts policy, as the student
 * sees it. Solved tasks and exhausted tasks read as such.
 */
function AttemptsBadge({
  used, left, max, locked, solved, policy, next,
}: {
  used: number; left: number | null; max: number; locked: boolean; solved: boolean;
  policy: GradingPolicy | null; next: number | null;
}) {
  if (solved) {
    return (
      <Pill variant="success" size="xs">
        solved{used > 1 ? ` on try ${used}` : ''}
      </Pill>
    );
  }
  if (locked) {
    return (
      <Pill variant="danger" size="xs">
        out of attempts
      </Pill>
    );
  }
  const tries = max > 0 ? `attempt ${used + 1} of ${max}` : `attempt ${used + 1}`;
  const deduct = policy?.deduct_on_fail && policy.deduction_pct > 0 ? ` · −${policy.deduction_pct}% per miss` : '';
  const worth = next != null ? ` · worth ${next}` : '';
  return (
    <Pill variant={left !== null && left <= 1 ? 'warn' : 'neutral'} size="xs">
      {tries}{deduct}{worth}
    </Pill>
  );
}

